import { createMutation, createLazyResource } from '@magicdoorinc/ui';
import { createSignal } from 'solid-js';
import { createStore } from 'solid-js/store';
import { createMagicDoorContext } from '~/contexts/utils';
import { LeasesRenewalsRepository } from '~/repositories/leasesRenewalsRepository';
import type {
  CreateLeaseRenewalOfferAutomaticSettingDto,
  LeaseRenewalOfferAutomaticSettingDto,
  LeaseRenewalOfferAutomaticSettingGroupsDto,
  PortfolioGroupDto,
  PropertyGroupDto,
} from '~/swagger/portal/Api';

const repository = new LeasesRenewalsRepository();

function checkPortfolioGroupType(
  item: PortfolioGroupDto | PropertyGroupDto | LeaseRenewalOfferAutomaticSettingDto
): item is PortfolioGroupDto {
  return 'portfolio' in item;
}

function checkPropertyGroupType(
  item: PortfolioGroupDto | PropertyGroupDto | LeaseRenewalOfferAutomaticSettingDto
): item is PropertyGroupDto {
  return 'property' in item;
}

export const [AutomaticLeaseRenewalProvider, useAutomaticLeaseRenewal] = createMagicDoorContext('AutomaticLeaseRenewal', () => {
  const [entity, setEntity] = createSignal<{
    type: keyof LeaseRenewalOfferAutomaticSettingGroupsDto;
    id?: string;
  }>();

  const [addChargeModal, setAddChargeModal] = createStore<{
    visible: boolean;
    editCharge?: any;
  }>({
    visible: false,
    editCharge: undefined,
  });

  const currentEntitySettings = () => {
    const _type = entity()?.type;
    if (!_type) return [];

    if (_type === 'global') {
      return automaticSettings()?.global;
    }

    const setting = automaticSettings()?.[_type]?.find((item) => {
      if (checkPortfolioGroupType(item)) {
        return item.portfolio.id === entity()?.id;
      } else if (checkPropertyGroupType(item)) {
        return item.property.id === entity()?.id;
      }
    });

    return setting?.settings;
  };

  const getSettingById = (id: string) => {
    return currentEntitySettings()?.find((item) => item.id === id);
  };

  const [automaticSettings, { refetch: fetchAutomaticSettings }] = createLazyResource(async () => {
    return await repository.getAutomaticSettings();
  });

  const createAutomaticSetting = createMutation(async (data: CreateLeaseRenewalOfferAutomaticSettingDto) => {
    return await repository.createAutomaticSetting(data);
  });

  const deleteAutomaticSetting = createMutation(async (id: string) => {
    await repository.deleteAutomaticSetting(id);
    fetchAutomaticSettings();
  });

  const deleteAllAutomaticSettings = createMutation(async () => {
    const _allSettings = currentEntitySettings();
    if (!_allSettings?.length) return;
    await Promise.all(_allSettings?.map((item) => deleteAutomaticSetting(item.id)));
    fetchAutomaticSettings();
  });

  const handleCreateAutomaticSetting = async (data: CreateLeaseRenewalOfferAutomaticSettingDto) => {
    await createAutomaticSetting({
      ...data,
      [`${entity()?.type}Id`]: entity()?.id,
    });

    fetchAutomaticSettings();
  };

  return {
    automaticSettings,
    fetchAutomaticSettings,
    deleteAutomaticSetting,
    setEntity,
    deleteAllAutomaticSettings,
    addChargeModal,
    setAddChargeModal,
    handleCreateAutomaticSetting,
    currentEntitySettings,
    getSettingById,
  };
});
